/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 * Component for select multi items
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @author Viacheslav Smalenski
 * @author Ilya Baranchikov
 * @version 1.29.1
 * @since 1.0.0
 */
import React, { createRef, PureComponent } from 'react';
import type { ChangeEvent, ReactElement } from 'react';

import invoke from 'lodash/invoke';
import { isMobile } from 'react-device-detect';

import { TOverflow } from '@/main/types/globalTypes';
import { icons } from '@assets/index';
import { KeyboardKeyCodeEnum } from '@modules/app/appConstants';
import RenderInBody from '@modules/app/hoc/RenderInBody';
import withDefaultProps from '@modules/app/hoc/withDefaultProps';
import withWindowEvents, { IWindowEvent } from '@modules/app/hoc/WithWindowEvents';
import { initTranslation } from '@modules/internationalization/constants';
import { ICommonTranslation } from '@modules/internationalization/types/index';
import { Loader } from '@modules/loader/components/Loader';

import { ILabelColor } from '../../Table/constants/labelColor';
import { DropDownFilterButtonSize, reachedBorder } from '../DropDownFilter/DropDownFilter';
import { Button, EllipseText, Input, LinkBlock } from '../index';
import { EInputKind, EInputType, IInputProps } from '../Input/Input';
import { IOptionItemProps } from '../Select/Select';
import { SelectDropStyled } from '../Select/styled/SelectDrop';
import ArrowTooltip from '../Tooltip/Tooltip';
import MultiSelectItemList from './components/MultiSelectItemList';
import {
    MultiSelectButtonsContainer,
    MultiSelectContainerStyled,
    MultiSelectDropDownStyled,
} from './styled/MultiSelectStyled';

export interface ISelectVirtualListProps {
    rowHeight: number;
    hasMore: boolean;
    isLoading: boolean;
    onScrollEvent: (searchValue: string) => void;
}

export interface IMSOptionItemProps extends IOptionItemProps {
    checked?: boolean;
}

export interface IMultiSelectLabelProps extends IOptionItemProps {
    color: ILabelColor;
    searchValue: string;
}

export type MultiSelectItemProps = IOptionItemProps | IMultiSelectLabelProps;

export interface IMultiSelectProps {
    fullWide?: boolean;
    virtualListProps?: ISelectVirtualListProps;
    filterButtonSize?: DropDownFilterButtonSize;
    input?: IInputProps;
    isSuggest?: boolean;
    kind: EInputKind;
    options: IOptionItemProps[];
    placeholder?: string;
    searchInputPlaceholder?: string;
    searchInputTooltipMsg?: string;
    searchInputType?: EInputKind;
    selectedOptions: IOptionItemProps[];
    suggestIsLoading?: boolean;
    translation: ICommonTranslation;
    tooltipOverflow?: TOverflow;
    tooltipMaxHeight?: string;
    filterInputHandlerCallback?: (searchValue: string) => (item: IMSOptionItemProps) => boolean;
    onClose?: () => void;
    onSearchInputChange?: (value: string) => void;
    onOpenAndClose?: (isOpen: boolean) => void;
    onSelect: (args: IOptionItemProps[]) => void;
    renderOptions?: (props: MultiSelectItemProps) => ReactElement;
    showSearch?: boolean;
}

export interface IMultiSelectState {
    componentLeft: number | null;
    firstUncheckedIndex?: number;
    hasInputText: boolean;
    left: number;
    opened: boolean;
    options: IOptionItemProps[];
    searchValue: string;
    selectedOptions: IMSOptionItemProps[];
    selectedValue: string | undefined;
    top: number;
    width: number | string;
    isUserTyping?: boolean;
}

const defaultProps: Partial<IMultiSelectProps> = {
    placeholder: '',
    kind: EInputKind.normal,
    options: [],
    translation: initTranslation.common,
    selectedOptions: [],
    showSearch: true,
};

export class MultiSelect extends PureComponent<IMultiSelectProps, IMultiSelectState> {
    static defaultProps = defaultProps;

    getSelectedValue = (list: IOptionItemProps[]) => {
        if (list.length === 1) {
            return `${list[0].label}`;
        }
        if (list.length === 0) {
            return '';
        }
        return `Выбрано ${list.length}`;
    };

    private innerOptions: IMSOptionItemProps[] = [];

    state = {
        componentLeft: null,
        firstUncheckedIndex: undefined,
        hasInputText: false,
        left: 0,
        opened: false,
        options: this.innerOptions,
        searchValue: '',
        selectedOptions: this.props.selectedOptions,
        selectedValue: this.getSelectedValue(this.props.selectedOptions),
        top: 0,
        width: 'auto',
        isUserTyping: false,
    };

    private componentRef: React.RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
    private dropdownRef: React.RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
    private selectDropRef: React.RefObject<HTMLDivElement> = createRef<HTMLDivElement>();

    /**
     * Method return string for multiselect input
     * @param list
     */

    componentDidUpdate(prevProps: Readonly<IMultiSelectProps>, prevState: IMultiSelectState) {
        if (prevProps !== this.props) {
            this.setState({
                isUserTyping: false,
            });
        }
        if (prevProps.options !== this.props.options) {
            this.setState((state, props) => {
                if (state.opened) {
                    const selectedOptions = this.props.selectedOptions.map((item: IMSOptionItemProps) => {
                        return {
                            ...item,
                            checked: true,
                        };
                    });

                    const unselectedOptions = props.options
                        .filter((item: IMSOptionItemProps) => {
                            return !this.props.selectedOptions.some((i: IOptionItemProps) => i.value === item.value);
                        })
                        .map((item: IMSOptionItemProps) => {
                            return {
                                ...item,
                                checked: false,
                            };
                        });
                    this.innerOptions = [...selectedOptions, ...unselectedOptions];
                }
                return {
                    options: this.innerOptions,
                };
            });
        }
        if (prevProps.selectedOptions !== this.props.selectedOptions) {
            this.setState((state, props) => {
                const value = this.getSelectedValue(props.selectedOptions);
                return {
                    hasInputText: value !== '',
                    selectedOptions: props.selectedOptions,
                    selectedValue: value,
                };
            });
        }
        if (!prevState.opened && this.state.opened) {
            this.correctDropdownLocation();
        }
        if (prevState.options !== this.state.options) {
            this.createBorder();
        }

        if (!prevState.opened && this.state.opened && this.props.onOpenAndClose) {
            this.props.onOpenAndClose(this.state.opened);
        }
        if (prevState.opened && !this.state.opened && this.props.onOpenAndClose) {
            this.props.onOpenAndClose(this.state.opened);
        }
    }

    /**
     * Method close list,
     * if withSave true then the selected options is saved and the props onSelect is called
     * else selected options reset to initial state
     * @param withSave
     */
    closeList(withSave: boolean): void {
        if (withSave) {
            this.setState(
                () => {
                    return {
                        hasInputText: this.state.selectedOptions.length !== 0,
                        opened: false,
                    };
                },
                () => {
                    this.props.onSelect(
                        this.state.selectedOptions.map((item: IMSOptionItemProps) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            const { checked, ...rest } = item;
                            return rest;
                        }),
                    );
                },
            );
        } else {
            this.setState((state, props) => {
                return {
                    opened: false,
                    selectedOptions: props.selectedOptions,
                    options: props.options,
                    selectedValue: this.getSelectedValue(props.selectedOptions),
                };
            });
            invoke(this.props, 'onClose');
        }
        window.removeEventListener('resize', this.toggleList, true);
    }

    /**
     * Check outside click, and close list if true
     * @param event
     */
    handleClickOutside = (event: MouseEvent): void => {
        if (
            this.componentRef.current &&
            !this.componentRef.current.contains(event.target as Node) &&
            this.dropdownRef.current &&
            !this.dropdownRef.current.contains(event.target as Node) &&
            this.state.opened
        ) {
            this.closeList(false);
        }
    };

    /**
     * Method apply select and close list, after enter press
     * @param event
     */
    windowKeyDownHandler = (event: KeyboardEvent): void => {
        if (
            this.componentRef.current &&
            !this.componentRef.current.contains(event.target as Node) &&
            this.state.opened
        ) {
            if (
                this.dropdownRef.current &&
                !this.dropdownRef.current.contains(event.target as Node) &&
                event.keyCode === KeyboardKeyCodeEnum.ENTER
            ) {
                this.closeList(true);
            } else if (
                this.dropdownRef.current &&
                this.dropdownRef.current.contains(event.target as Node) &&
                event.keyCode === KeyboardKeyCodeEnum.ESCAPE
            ) {
                this.closeList(false);
            }
        }
    };

    keyDownHandler = (event: React.KeyboardEvent): void => {
        if (event.keyCode === KeyboardKeyCodeEnum.ENTER) {
            this.toggleList();
        } else if (event.keyCode === KeyboardKeyCodeEnum.ESCAPE) {
            this.closeList(false);
        }
    };

    /**
     * Select item and and change select value
     * @param selectedOption
     */
    selectItem = (selectedOption: IOptionItemProps): void => {
        this.setState((state) => {
            const newItem = !state.selectedOptions.some((i) => i.value === selectedOption.value);
            const selectedOptions = newItem
                ? [selectedOption, ...state.selectedOptions]
                : state.selectedOptions.filter((i) => i.value !== selectedOption.value);
            const selectedValue = this.getSelectedValue(selectedOptions);
            return {
                selectedOptions,
                selectedValue,
            };
        });
    };

    /**
     * Toggle list state
     */
    toggleList = (): void => {
        this.setState(
            (state, props) => {
                const opened = !state.opened;
                let componentLeft: number | null = null;
                if (opened) {
                    const selectedOptions = this.props.selectedOptions.map((item: IMSOptionItemProps) => {
                        return {
                            ...item,
                            checked: true,
                        };
                    });

                    const unselectedOptions = props.options
                        .filter((item: IMSOptionItemProps) => {
                            return !this.props.selectedOptions.some((i: IOptionItemProps) => i.value === item.value);
                        })
                        .map((item: IMSOptionItemProps) => {
                            return {
                                ...item,
                                checked: false,
                            };
                        });
                    this.innerOptions = [...selectedOptions, ...unselectedOptions];

                    componentLeft = this.componentRef.current?.getBoundingClientRect().left ?? null;
                }
                return {
                    componentLeft,
                    opened: opened,
                    options: this.innerOptions,
                    searchValue: props.searchInputPlaceholder || '',
                    selectedOptions: props.selectedOptions,
                    selectedValue: this.getSelectedValue(props.selectedOptions),
                };
            },
            () => {
                if (this.state.opened) {
                    this.createBorder();
                } else {
                    invoke(this.selectDropRef, 'current.blur');
                }

                if (this.state.opened && !isMobile) {
                    window.addEventListener('resize', this.toggleList, true);
                } else {
                    window.removeEventListener('resize', this.toggleList, true);
                }
            },
        );
    };
    /**
     * Method find first uncheked item and add top border
     */
    createBorder = () => {
        const firstUncheckedIndex = this.state.options.findIndex((item, index) => {
            return index !== 0 && this.state.options[index - 1].checked && !item.checked;
        });
        this.setState({
            firstUncheckedIndex,
        });
    };
    /**
     * Method filter props.options list, change state.options value
     * @param event
     */
    filterInputHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = String(event.target.value);

        if (this.state.opened) {
            invoke(this.props, 'onSearchInputChange', value);

            // do not filter if there is server filtering
            if (!!this.props.virtualListProps) {
                this.setState({
                    searchValue: value,
                });
                return;
            }

            this.setState(() => {
                const filterCallback =
                    this.props.filterInputHandlerCallback?.(value) ||
                    ((item) => item.label.toLowerCase().includes(value.toLowerCase()));
                const options = this.innerOptions.filter(filterCallback);

                return {
                    options,
                    searchValue: value,
                    isUserTyping: !!this.props.isSuggest,
                };
            });
        }
    };

    /**
     * Method prevent scroll while wheel event if cursor inside filter
     */
    wheelHandler = (event: WheelEvent) => {
        if (
            this.componentRef.current &&
            !this.componentRef.current.contains(event.target as Node) &&
            this.dropdownRef.current &&
            this.dropdownRef.current.contains(event.target as Node) &&
            this.state.opened
        ) {
            const list = invoke(event, 'target.closest', '.list');
            if (reachedBorder(list, event.deltaY)) {
                event.preventDefault();
            }
        }
    };

    /**
     * Method correct location if scroll event occurs
     */
    scrollHandler = (event: MouseEvent) => {
        this.dropdownRef.current && !this.dropdownRef.current.contains(event.target as Node) && this.state.opened
            ? this.correctDropdownLocation()
            : null;
    };

    /**
     * Method correct list coordinates according component location
     */
    correctDropdownLocation = () => {
        if (!this.dropdownRef.current || !this.componentRef.current) {
            return;
        }
        const { height: dropdownHeight, width: dropdownWidth } = this.dropdownRef.current.getBoundingClientRect();
        const {
            height: componentHeight,
            width: componentWidth,
            top: componentTop,
            left: componentLeft,
        } = this.componentRef.current.getBoundingClientRect();

        // Hide dropdown on horizontal scroll
        if (this.state.componentLeft !== null && componentLeft !== this.state.componentLeft && !isMobile) {
            this.toggleList();
            return;
        }

        const left =
            componentLeft + dropdownWidth > window.innerWidth
                ? componentLeft + componentWidth - dropdownWidth
                : componentLeft;
        const top =
            componentTop + componentHeight + dropdownHeight < window.innerHeight
                ? componentTop + componentHeight
                : componentTop > dropdownHeight
                ? componentTop - dropdownHeight
                : 0;

        this.setState({
            top,
            left,
            width: this.props.fullWide ? `${componentWidth}px` : 'auto',
        });
    };

    onEnterHandler = () => {
        if (this.props.searchInputType === EInputKind.error) return;

        this.setState(
            () => {
                return {
                    hasInputText: !!this.state.options.length,
                    opened: false,
                    selectedValue: this.getSelectedValue(this.state.options),
                };
            },
            () => {
                this.props.onSelect(
                    this.state.options.map((item: IMSOptionItemProps) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { checked, ...rest } = item;
                        return rest;
                    }),
                );
            },
        );
    };

    render() {
        const {
            filterButtonSize,
            input,
            kind,
            placeholder,
            renderOptions,
            searchInputPlaceholder,
            searchInputTooltipMsg,
            searchInputType,
            translation,
            tooltipMaxHeight,
            tooltipOverflow,
            virtualListProps,
            fullWide,
        } = this.props;

        const {
            firstUncheckedIndex,
            left,
            opened,
            options,
            searchValue,
            selectedOptions,
            selectedValue,
            top,
            width,
            isUserTyping,
        } = this.state;

        const multiSelectClassName = opened ? 'multiselect_select-opened' : '';

        const listProps = virtualListProps
            ? {
                  ...virtualListProps,
                  onScrollEvent: () => {
                      virtualListProps.onScrollEvent(searchValue);
                  },
              }
            : undefined;

        const inputKind =
            kind === EInputKind.error
                ? EInputKind.error
                : this.state.hasInputText
                ? EInputKind.filled
                : EInputKind.normal;

        const selectKind =
            kind === EInputKind.error ? EInputKind.error : selectedValue ? EInputKind.filled : EInputKind.normal;

        return (
            <MultiSelectContainerStyled className={`multiselect ${multiSelectClassName}`} ref={this.componentRef}>
                <SelectDropStyled
                    className={opened ? 'opened' : 'closed'}
                    icon={icons.arrowListDownSmall}
                    kind={selectKind}
                    ref={this.selectDropRef}
                    selectDropSize={filterButtonSize}
                    onClick={this.toggleList}
                    onKeyDown={this.keyDownHandler}
                >
                    {selectedValue ? (
                        <EllipseText title={selectedValue}>{selectedValue}</EllipseText>
                    ) : (
                        <span className="multiselect__select-placeholder">
                            {placeholder ? placeholder : translation.multiSelect.placeholder}
                        </span>
                    )}
                </SelectDropStyled>

                <Input
                    {...input}
                    readOnly
                    className="multiselect__select-input"
                    kind={inputKind}
                    righticonhandler={this.toggleList}
                    value={selectedValue}
                    onClick={this.toggleList}
                />
                {opened && (
                    <RenderInBody>
                        <MultiSelectDropDownStyled
                            className="multiselect__select-block"
                            fullWide={fullWide}
                            hasInputText={this.state.hasInputText}
                            kind={EInputKind.normal}
                            ref={this.dropdownRef}
                            style={{ left, top, width }}
                        >
                            {virtualListProps?.isLoading && <Loader backdropOpacity={0.7} showLoader={true} />}
                            {this.props.showSearch && (
                                <ArrowTooltip placement="top" title={searchInputTooltipMsg}>
                                    <Input
                                        autoFocus={true}
                                        className="multiselect__search-input"
                                        data-input="multiselectSearch"
                                        kind={searchInputType || EInputKind.normal}
                                        placeholder={
                                            searchInputPlaceholder
                                                ? searchInputPlaceholder
                                                : translation.multiSelect.searchPlaceholder
                                        }
                                        righticon={icons.searchSmallIcon}
                                        type={EInputType.small}
                                        value={searchValue}
                                        onChange={this.filterInputHandler}
                                        onEnter={this.onEnterHandler}
                                    />
                                </ArrowTooltip>
                            )}
                            <MultiSelectItemList
                                firstUncheckedIndex={firstUncheckedIndex}
                                isSuggest={this.props.isSuggest}
                                kind={EInputKind.normal}
                                options={options}
                                render={renderOptions}
                                searchValue={searchValue}
                                selectedOptions={selectedOptions}
                                suggestIsLoading={this.props.suggestIsLoading || isUserTyping}
                                tooltipMaxHeight={tooltipMaxHeight}
                                tooltipOverflow={tooltipOverflow}
                                translation={translation}
                                virtualListProps={listProps}
                                onSelect={this.selectItem}
                            />
                            <MultiSelectButtonsContainer>
                                <Button
                                    className="multiselect__ok-button"
                                    dataName="ok"
                                    disabled={searchInputType === EInputKind.error}
                                    kind="medium"
                                    onClick={() => this.closeList(true)}
                                >
                                    {translation.multiSelect.OK}
                                </Button>
                                <LinkBlock dataName="close" onClick={() => this.closeList(false)}>
                                    {translation.multiSelect.close}
                                </LinkBlock>
                            </MultiSelectButtonsContainer>
                        </MultiSelectDropDownStyled>
                    </RenderInBody>
                )}
            </MultiSelectContainerStyled>
        );
    }
}

const selectFilterEvents: IWindowEvent[] = [
    {
        event: 'mousedown',
        handler: 'handleClickOutside',
    },
    {
        event: 'keydown',
        handler: 'windowKeyDownHandler',
    },
    {
        event: 'scroll',
        handler: 'scrollHandler',
    },
    {
        event: 'wheel',
        handler: 'wheelHandler',
        options: {
            passive: false,
        },
    },
];

export default withDefaultProps(defaultProps, withWindowEvents(MultiSelect, selectFilterEvents));
